// Колоните на „Монитори" — КЛИЕНТСКИ режим: `GET /reports/analytics/
// kitchen-monitors` връща целия период наведнъж, затова сортирането,
// филтрите и редът „Общо" се смятат в браузъра.
//
// Старите `<Select>`-и над таблицата (станция, оператор) са тук като филтри в
// самите колони. Опциите им идват от фасетите на таблицата — стойностите са
// низове и наборът е целият период, тоест същият списък, който събираше
// старият екран. Колоната „Тип" носи ОБЕКТ (чип) и нейните опции се подават
// изрично: фасет по обект не се сортира и не се сравнява.
//
// Времената по статуси са СЕКУНДИ. Колоната държи числото (за подредбата), а
// клетката показва „4m 05s" — иначе таблицата се подрежда по текст и „9s"
// застава над „10m 00s".

import { useMemo } from "react";
import { createColumnHelper, type Row } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { parseDecimal } from "../../../../components/dataTable";
import type { ChecklistOption, ChipCellValue } from "../../../../components/dataTable";
import type { KitchenMonitorReportRow } from "../../../../api/reportsKitchenMonitors";
import { formatDuration, formatMoment } from "./kitchenMonitorsFormat";

const helper = createColumnHelper<KitchenMonitorReportRow>();

/** Секундите на един статус; `null` = редът не е минал през него. */
type SecondsPicker = (row: KitchenMonitorReportRow) => number | null;

type Options = {
  /** i18n ключът на „Добавка". */
  addonKey: string;
  /** i18n ключът на продаваемото — „Асортимент" или „Рецепта" (склад). */
  sellableKey: string;
};

/**
 * Клетка „продължителност": числото се подрежда, текстът се чете.
 *
 * Нарочно е функция за рендер, а не компонент: файлът изнася hook, а един
 * експортиран компонент до него чупи fast refresh (react-refresh правилото).
 */
const renderDuration = (seconds: number | null | undefined) => (
  <span
    className={`font-pos-mono text-[12.5px] font-semibold tabular-nums ${
      seconds === null || seconds === undefined ? "text-faint" : "text-ink"
    }`}
  >
    {formatDuration(seconds)}
  </span>
);

/**
 * Една колона с време по статус.
 *
 * Компараторът е СОБСТВЕН и повтаря стария: липсващият статус се държи като
 * −1, тоест стои преди нулевите секунди при възходяща подредба. Без него
 * `null` минава през общия компаратор и редовете без статус се разпиляват.
 */
const durationColumn = (
  id: string,
  header: string,
  pickSeconds: SecondsPicker,
) =>
  helper.accessor(pickSeconds, {
    id,
    header,
    size: 112,
    sortingFn: (
      rowA: Row<KitchenMonitorReportRow>,
      rowB: Row<KitchenMonitorReportRow>,
    ) =>
      (pickSeconds(rowA.original) ?? -1) - (pickSeconds(rowB.original) ?? -1),
    cell: (context) => renderDuration(context.getValue()),
    meta: { align: "right", excel: { format: "text" } },
  });

export const useKitchenMonitorsColumns = ({
  addonKey,
  sellableKey,
}: Options) => {
  const { t } = useTranslation();

  // „Тип" е затворен списък от две стойности — и двете стоят в чеклиста, дори
  // когато периодът е само с добавки.
  const kindOptions = useMemo<ChecklistOption[]>(
    () => [
      { value: t(sellableKey), label: t(sellableKey) },
      { value: t(addonKey), label: t(addonKey) },
    ],
    [t, addonKey, sellableKey],
  );

  return useMemo(
    () => [
      helper.accessor((row) => row.bill_name, {
        id: "bill_name",
        header: t("reportsPage.kmTable"),
        size: 128,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "text" },
          alwaysVisible: true,
          mobileRole: "title",
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => row.item_name, {
        id: "item_name",
        header: t("reportsPage.product"),
        size: 220,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "text" },
          alwaysVisible: true,
          mobileRole: "strong",
          excel: { format: "text" },
        },
      }),

      helper.accessor(
        (row): ChipCellValue => ({
          tone: row.is_addition ? "neutral" : "brand",
          label: t(row.is_addition ? addonKey : sellableKey),
        }),
        {
          id: "kind",
          header: t("reportsPage.kmType"),
          size: 116,
          meta: {
            cell: { kind: "chip" },
            filter: { kind: "checklist", options: kindOptions },
            hideBelow: "tablet",
            excel: { format: "text" },
          },
        },
      ),

      helper.accessor((row) => row.station_name, {
        id: "station_name",
        header: t("reportsPage.kmStation"),
        size: 140,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist" },
          excel: { format: "text" },
        },
      }),

      // Decimal от BE идва като JSON НИЗ — парсва се явно. БЕЗ `decimals`:
      // теглените артикули носят дробно количество и закръглянето би показало
      // 0.750 кг като „1".
      helper.accessor((row) => parseDecimal(row.quantity), {
        id: "quantity",
        header: t("reportsPage.kmQty"),
        size: 76,
        meta: {
          cell: { kind: "num" },
          total: "sum",
          excel: { format: "number" },
        },
      }),

      // Часът е СЪС секунди — затова колоната не е декларативна `dt` клетка
      // (тя показва само час:минута).
      helper.accessor((row) => row.ordered_at, {
        id: "ordered_at",
        header: t("reportsPage.kmEnteredAt"),
        size: 108,
        cell: (context) => (
          <span className="font-pos-mono text-[12.5px] font-semibold tabular-nums text-ink">
            {formatMoment(context.getValue())}
          </span>
        ),
        meta: { align: "right", excel: { format: "text" } },
      }),

      durationColumn(
        "seconds_to_start",
        t("reportsPage.kmStatusInProgress"),
        (row) => row.seconds_to_start,
      ),
      durationColumn(
        "seconds_cooking",
        t("reportsPage.kmStatusCooked"),
        (row) => row.seconds_cooking,
      ),
      durationColumn(
        "seconds_to_issue",
        t("reportsPage.kmStatusIssued"),
        (row) => row.seconds_to_issue,
      ),
      durationColumn(
        "seconds_to_serve",
        t("reportsPage.kmStatusServed"),
        (row) => row.seconds_to_serve,
      ),

      // Кой е бутнал реда напред. Старият екран ги показваше под времената, в
      // същата клетка; тук всяко име е собствена колона, за да има филтър и да
      // се изнася поотделно. Празната стойност се рисува с тире и НЕ влиза
      // като опция във фунията (фасетите изхвърлят празния низ).
      ...operatorColumns(t),
    ],
    [t, addonKey, sellableKey, kindOptions],
  );
};

/** Четирите „кой го направи" колони — един и същ вид, различно поле. */
const operatorColumns = (t: (key: string) => string) =>
  [
    {
      id: "started_by",
      header: t("reportsPage.kmStartedBy"),
      pickOperator: (row: KitchenMonitorReportRow) => row.started_by,
    },
    {
      id: "cooked_by",
      header: t("reportsPage.kmCookedBy"),
      pickOperator: (row: KitchenMonitorReportRow) => row.cooked_by,
    },
    {
      id: "issued_by",
      header: t("reportsPage.kmIssuedBy"),
      pickOperator: (row: KitchenMonitorReportRow) => row.issued_by,
    },
    {
      id: "served_by",
      header: t("reportsPage.kmServedBy"),
      pickOperator: (row: KitchenMonitorReportRow) => row.served_by,
    },
  ].map(({ id, header, pickOperator }) =>
    helper.accessor((row) => pickOperator(row) ?? "", {
      id,
      header,
      size: 128,
      meta: {
        cell: { kind: "text" },
        filter: { kind: "checklist" },
        hideBelow: "tablet",
        excel: { format: "text" },
      },
    }),
  );
